import 'styled-components/macro';

import {
  ContextBubble,
  ContextBubbleAnimation,
  ContextBubbleBackground,
} from '@homex/se-react-components';
import React from 'react';

import { ThemedCircleButton } from 'components/ThemedCircleButton';
import { ReactComponent as MediaUploadFileSvg } from 'components/svg/MediaUploadFile.svg';
import { ReactComponent as MediaUploadPhotoSvg } from 'components/svg/MediaUploadPhoto.svg';
import { ReactComponent as MediaUploadVideoSvg } from 'components/svg/MediaUploadVideo.svg';
import {
  CameraDestinationType,
  CameraMediaType,
  CameraSourceType,
  IAndroidCameraContextBubbleProps,
  ICameraOptions,
  IFileError,
  ILocalFile,
  IMediaFile,
  LocalFileSystemAccessType,
} from 'typings';
import cordovaApi from 'utils/cordovaApi';

import {
  ButtonContainer,
  bubbleStyles,
  buttonStyles,
  fileSvgStyles,
  photoSvgStyles,
  videoSvgStyles,
} from './styles';

export class AndroidCameraContextBubble extends React.Component<IAndroidCameraContextBubbleProps> {
  private contextBubbleRef: ContextBubble | null = null;
  private baseCameraOptions: ICameraOptions = {
    correctOrientation: true,
    destinationType: CameraDestinationType.FILE_URI,
    // These are the largest dimension displayed in the media preview lightbox in se-client-dashboard
    targetHeight: 882,
    targetWidth: 882,
  };

  componentDidUpdate(prevProps: IAndroidCameraContextBubbleProps) {
    const { isVisible: prevIsVisible } = prevProps;
    const { isVisible } = this.props;

    if (isVisible !== prevIsVisible) {
      if (this.contextBubbleRef) {
        if (isVisible) {
          this.contextBubbleRef.doNextAnimation();
        } else {
          this.contextBubbleRef.close();
        }
      }
    }
  }

  handleCloseClick = () => {
    this.props.onFilesReceived([]);
  };

  handlePhotoCameraClick = () => {
    this.getPicture({
      ...this.baseCameraOptions,
      quality: 50,
      sourceType: CameraSourceType.CAMERA,
    });
  };

  handlePhotoLibraryClick = () => {
    this.getPicture({
      ...this.baseCameraOptions,
      mediaType: CameraMediaType.PICTURE,
      sourceType: CameraSourceType.SAVEDPHOTOALBUM,
    });
  };

  handleVideoCameraClick = async () => {
    try {
      await this.checkPermissions();

      cordovaApi.capture.captureVideo(
        async (mediaFiles: Array<IMediaFile>) => {
          const [mediaFile] = mediaFiles;

          if (mediaFile) {
            const videoFile = await this.resolveFile(mediaFile.localURL, {
              type: 'video/mp4',
            });
            this.props.onFilesReceived([videoFile]);
          }
        },
        (_err: Error) => {
          // Do nothing
        },
        {
          duration: 30,
          limit: 1,
        },
      );
    } catch {
      // Do nothing
    }
  };

  handleVideoLibraryClick = async () => {
    try {
      await this.checkPermissions();

      cordovaApi.camera.getPicture(
        async (data: string) => {
          const videoFile = await this.resolveFile(`file://${data}`, {
            type: 'video/mp4',
          });
          this.props.onFilesReceived([videoFile]);
        },
        (_message: string) => {
          // Do nothing
        },
        {
          destinationType: CameraDestinationType.FILE_URI,
          mediaType: CameraMediaType.VIDEO,
          sourceType: CameraSourceType.SAVEDPHOTOALBUM,
        },
      );
    } catch {
      // Do nothing
    }
  };

  private async getPicture(options: ICameraOptions) {
    try {
      await this.checkPermissions();

      cordovaApi.camera.getPicture(
        async (data: string) => {
          const imageFile = await this.resolveFile(data);
          this.props.onFilesReceived([imageFile]);
        },
        (_message: string) => {
          // Do nothing
        },
        options,
      );
    } catch {
      // Do nothing
    }
  }

  /**
   * create a file to force permissions to be checked
   */
  private async checkPermissions(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      cordovaApi.requestFileSystem(
        LocalFileSystemAccessType.PERSISTENT,
        0,
        (fs: any) => {
          fs.root.getFile(
            'newPersistentFile.txt',
            { create: true, exclusive: false },
            (_fileEntry: any) => {
              resolve(true);
            },
            reject,
          );
        },
        reject,
      );
    });
  }

  private async resolveFile(
    path: string | null,
    options?: FilePropertyBag,
  ): Promise<File> {
    return new Promise<File>((resolve, reject) => {
      if (!path) {
        return reject(new Error('Path is empty'));
      }

      cordovaApi.resolveLocalFileSystemURL(
        path,
        (localFile: ILocalFile) => {
          localFile.file(
            (file: File) => {
              const reader = new FileReader();

              reader.onerror = (err) => {
                reject(err);
              };

              reader.onloadend = (event) => {
                if (!reader.result) {
                  reject(event);
                } else {
                  const domFile = new cordovaApi.DomFile(
                    [reader.result],
                    path,
                    options,
                  );
                  (domFile as any).preview = URL.createObjectURL(domFile);

                  resolve(domFile);
                }
              };

              reader.readAsArrayBuffer(file);
            },
            (err: IFileError) => {
              reject(err);
            },
          );
        },
        (err: any) => {
          reject(err);
        },
      );
    });
  }

  render() {
    return (
      <ContextBubble
        animation={ContextBubbleAnimation.FADE_IN}
        background={ContextBubbleBackground.INVERT}
        css={bubbleStyles}
        isClosableByBlur={true}
        isClosableByX={true}
        isOpenByDefault={false}
        onCloseClick={this.handleCloseClick}
        reclickBehavior="reshow"
        ref={(ref) => (this.contextBubbleRef = ref)}
      >
        <ButtonContainer>
          <ThemedCircleButton
            css={buttonStyles}
            icon={<MediaUploadVideoSvg css={videoSvgStyles} />}
            label="Record video"
            labelAlign="bottom"
            onClick={this.handleVideoCameraClick}
          />
          <ThemedCircleButton
            css={buttonStyles}
            icon={<MediaUploadPhotoSvg css={photoSvgStyles} />}
            label="Shoot photos"
            labelAlign="bottom"
            onClick={this.handlePhotoCameraClick}
          />
          <ThemedCircleButton
            css={buttonStyles}
            icon={<MediaUploadFileSvg css={fileSvgStyles} />}
            label="Browse video"
            labelAlign="bottom"
            onClick={this.handleVideoLibraryClick}
          />
          <ThemedCircleButton
            css={buttonStyles}
            icon={<MediaUploadFileSvg css={fileSvgStyles} />}
            label="Browse photos"
            labelAlign="bottom"
            onClick={this.handlePhotoLibraryClick}
          />
        </ButtonContainer>
      </ContextBubble>
    );
  }
}
